/**
 * Magic Link Page — Initiates token verification and displays status.
 *
 * Reads :token from the URL, calls processMagicLink() once on mount,
 * then reacts to magicLinkStatus changes from AuthContext.
 */

import { useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { Loader2, XCircle } from 'lucide-react';
import { Button } from '@/components/ui/Button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/Card';
import { useAuth } from '@/contexts/AuthContext';
import { OAUTH_PENDING_KEY } from './OAuthAuthorizePage';
import { DEVICE_PENDING_KEY } from './DeviceVerifyPage';
import { useDocumentTitle } from '@/hooks/useDocumentTitle';

// Must match the key written by GuestCheckoutForm when an existing-account email is detected.
const CHECKOUT_REDIRECT_KEY = 'guardrail_checkout_redirect';

export default function MagicLinkPage() {
  useDocumentTitle('Magic Link');
  const navigate = useNavigate();
  const { token } = useParams<{ token: string }>();
  const { authenticated, magicLinkStatus, magicLinkError, magicLinkRedirect, processMagicLink } =
    useAuth();

  // Initiate verification once on mount
  useEffect(() => {
    if (token) processMagicLink(token);
  }, [token]); // eslint-disable-line react-hooks/exhaustive-deps

  // After successful auth, resume any pending flow, otherwise go to dashboard.
  // Render a spinner instead of null to avoid a white flash during navigation.
  if (authenticated || magicLinkStatus === 'success') {
    const oauthPending = sessionStorage.getItem(OAUTH_PENDING_KEY);
    const devicePending = sessionStorage.getItem(DEVICE_PENDING_KEY);
    const checkoutPending = sessionStorage.getItem(CHECKOUT_REDIRECT_KEY);
    if (oauthPending) {
      // Don't remove yet — OAuthAuthorizePage reads params from sessionStorage
      // when the URL no longer has query params (post-login resume).
      // OAuthAuthorizePage will clean up after consuming them.
      queueMicrotask(() => navigate('/oauth/authorize', { replace: true }));
    } else if (devicePending) {
      sessionStorage.removeItem(DEVICE_PENDING_KEY);
      queueMicrotask(() => navigate(devicePending, { replace: true }));
    } else if (checkoutPending) {
      sessionStorage.removeItem(CHECKOUT_REDIRECT_KEY);
      // Validate path is an internal app route before navigating (guard against sessionStorage tampering).
      const safeRedirect = /^\/subscribe\/[a-z0-9-]+/.test(checkoutPending)
        ? checkoutPending
        : '/pricing';
      queueMicrotask(() => navigate(safeRedirect, { replace: true }));
    } else {
      // Fall back to login_redirect (set by LoginPage for ?return= flows)
      const loginRedirect = sessionStorage.getItem('login_redirect');
      if (loginRedirect) sessionStorage.removeItem('login_redirect');
      const redirectPath = magicLinkRedirect || loginRedirect || '/dashboard';
      queueMicrotask(() => navigate(redirectPath, { replace: true }));
    }
    return (
      <div className="min-h-screen bg-zinc-900 flex items-center justify-center">
        <Loader2 className="w-8 h-8 text-green-500 animate-spin" />
      </div>
    );
  }

  const getStatusIcon = () => {
    if (magicLinkStatus === 'processing' || magicLinkStatus === 'idle') {
      return <Loader2 className="w-8 h-8 text-blue-500 animate-spin" />;
    }
    return <XCircle className="w-8 h-8 text-red-500" />;
  };

  const getStatusTitle = () => {
    if (magicLinkStatus === 'processing' || magicLinkStatus === 'idle')
      return 'Verifying Magic Link';
    return 'Verification Failed';
  };

  const getStatusDescription = () => {
    if (magicLinkStatus === 'processing' || magicLinkStatus === 'idle') {
      return 'Please wait while we verify your magic link...';
    }
    return magicLinkError || 'There was a problem verifying your magic link.';
  };

  const getActionButton = () => {
    if (magicLinkStatus === 'error') {
      return (
        <Button
          onClick={() => navigate('/login')}
          className="bg-green-500 hover:bg-green-600 text-white"
        >
          Back to Login
        </Button>
      );
    }
    return null;
  };

  return (
    <div className="min-h-screen bg-zinc-900 flex items-center justify-center p-4">
      <div className="w-full max-w-md">
        {/* Status Card */}
        <Card className="bg-zinc-800 border-zinc-700">
          <CardHeader className="text-center">
            <div className="flex justify-center mb-4">{getStatusIcon()}</div>
            <CardTitle className="text-white">{getStatusTitle()}</CardTitle>
            <CardDescription className="text-zinc-400">{getStatusDescription()}</CardDescription>
          </CardHeader>
          {getActionButton() && (
            <CardContent className="text-center">{getActionButton()}</CardContent>
          )}
        </Card>

        {/* Footer */}
        <p className="text-center text-sm text-zinc-500 mt-6">
          Please try again or contact support if the issue persists.
        </p>
      </div>
    </div>
  );
}
